<template>
  <v-main>
    <v-container fluid class="pa-2 pa-sm-4">
      <!-- Back Button -->
      <v-btn
        fab
        x-small
        class="mx-1 mb-2"
        color="white"
        elevation="2"
        @click="$router.back()"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Main Card -->
      <v-card rounded="xl" color="blue lighten-5" class="pa-3 pa-sm-4">
        <v-form ref="form" v-model="valid" lazy-validation>
          <!-- Month and Year (Admin only) -->
          <v-row class="ma-0" v-if="user?.roles[0]?.name !== 'Client'">
            <v-col cols="12" sm="6" class="py-0 pr-sm-2">
              <v-toolbar-title class="text-body-2 text-sm-body-1"
                >Month</v-toolbar-title
              >
              <v-select
                v-model="selectedMonth"
                :items="months"
                label="Select Month"
                solo
                dense
                rounded
                hide-details
                :rules="[(v) => !!v || 'Month is required']"
                @change="updateForm"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" class="py-0 pl-sm-2">
              <v-toolbar-title class="text-body-2 text-sm-body-1"
                >Year</v-toolbar-title
              >
              <v-select
                v-model="selectedYear"
                :items="years"
                label="Select Year"
                solo
                dense
                rounded
                hide-details
                :rules="[(v) => !!v || 'Year is required']"
                @change="updateForm"
              ></v-select>
            </v-col>
          </v-row>

          <!-- Status -->
          <v-row class="ma-0 mt-4">
            <v-col cols="12" class="py-0">
              <v-toolbar-title class="text-body-2 text-sm-body-1"
                >Status</v-toolbar-title
              >
              <v-select
                v-model="form.status"
                :items="status"
                label="Select Status"
                solo
                dense
                rounded
                hide-details
                :rules="[(v) => !!v || 'Status is required']"
              ></v-select>
            </v-col>
          </v-row>

          <!-- Reject Reason -->
          <v-row class="ma-0 mt-4" v-if="form.status === 'Reject'">
            <v-col cols="12" class="py-0">
              <v-toolbar-title class="text-body-2 text-sm-body-1"
                >Reject Reason</v-toolbar-title
              >
              <v-text-field
                v-model="form.reject_reason"
                placeholder="Enter reject reason"
                solo
                dense
                rounded
                hide-details
                :rules="[(v) => !!v || 'Reject reason is required']"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Is Revise -->
          <v-row class="ma-0 mt-4">
            <v-col cols="6" class="py-0">
              <v-toolbar-title class="text-body-2 text-sm-body-1"
                >Is Revise</v-toolbar-title
              >
            </v-col>
            <v-col cols="6" class="py-0">
              <v-checkbox
                v-model="form.is_revise"
                label="Requires Revision"
                hide-details
              ></v-checkbox>
            </v-col>
          </v-row>

          <!-- Revise Reason -->
          <v-row class="ma-0 mt-4" v-if="form.is_revise">
            <v-col cols="12" class="py-0">
              <v-toolbar-title class="text-body-2 text-sm-body-1"
                >Revise Reason</v-toolbar-title
              >
              <v-text-field
                v-model="form.revise_reason"
                placeholder="Enter revise reason"
                solo
                dense
                rounded
                hide-details
                :rules="[(v) => !!v || 'Revise reason is required']"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- File Upload (Admin only) -->
          <v-row class="ma-0 mt-4" v-if="user?.roles[0]?.name !== 'Client'">
            <v-col cols="12" class="py-0">
              <v-toolbar-title class="text-body-2 text-sm-body-1"
                >Upload Document</v-toolbar-title
              >
              <v-file-input
                v-model="documentFile"
                accept=".pdf,.doc,.docx"
                placeholder="Select a file"
                prepend-inner-icon="mdi-paperclip"
                show-size
                solo
                dense
                rounded
                hide-details
                :rules="fileRules"
                @change="handleFileSelection"
              >
                <!-- class="text-truncate" -->
                <template v-slot:selection="{ text }">
                  <span class="text-truncate" style="max-width: 200px">{{
                    text
                  }}</span>
                </template>
              </v-file-input>
            </v-col>
          </v-row>

          <!-- Submit Button -->
          <v-row class="ma-0 mt-6 mb-4">
            <v-col align="center" class="py-0">
              <v-btn
                color="primary"
                rounded
                :loading="isLoading"
                :disabled="!valid || isLoading"
                @click="save"
              >
                Submit
              </v-btn>
            </v-col>
          </v-row>

          <!-- Snackbar -->
          <v-snackbar
            v-model="snackbar"
            :color="snackbarColor"
            timeout="3000"
            bottom
          >
            {{ snackbarText }}
            <template v-slot:action="{ attrs }">
              <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
            </template>
          </v-snackbar>
        </v-form>
      </v-card>
    </v-container>

    <!-- Footer -->
    <v-container class="footer-text" fluid>
      <v-row justify="center">
        <v-col align="center" cols="12">
          <h3 class="white--text text-body-2">We Alliance Sparkle</h3>
          <h4 class="white--text text-caption">v 1.0.0</h4>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "BillingForm",
  data() {
    return {
      valid: true,
      isLoading: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      selectedMonth: "",
      selectedYear: "",
      documentFile: null,
      mediaUrl: "https://your-api.com/storage/", // Replace with your actual base URL
      form: {
        status: "Pending",
        is_revise: false,
        reject_reason: "",
        revise_reason: "",
        month: "",
        year: "",
      },
      status: [
        { value: "Pending", text: "Pending" },
        { value: "Done", text: "Done" },
        { value: "Reject", text: "Reject" },
      ],
      months: [
        { text: "January", value: "01" },
        { text: "February", value: "02" },
        { text: "March", value: "03" },
        { text: "April", value: "04" },
        { text: "May", value: "05" },
        { text: "June", value: "06" },
        { text: "July", value: "07" },
        { text: "August", value: "08" },
        { text: "September", value: "09" },
        { text: "October", value: "10" },
        { text: "November", value: "11" },
        { text: "December", value: "12" },
      ],
      years: [],
      fileRules: [
        (v) => !v || !!v || "Document is required", // Optional for editing
        (v) => !v || v.size <= 5000000 || "File size must be less than 5MB",
      ],
    };
  },
  mounted() {
    this.setCurrentDate();
    this.getMasters();
    if (this.$route.params.billId) {
      this.getData();
    }
  },
  methods: {
    setCurrentDate() {
      const now = new Date();
      this.selectedMonth = String(now.getMonth() + 1).padStart(2, "0");
      this.selectedYear = String(now.getFullYear());
      this.updateForm();
    },
    async getMasters() {
      try {
        this.isLoading = true;
        const response = await axios.get("/masters");
        this.years = response.data.data.map((year) => ({
          text: String(year),
          value: String(year),
        }));
      } catch (error) {
        this.showSnackbar("Failed to load years", "error");
        console.error("Error fetching years:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async getData() {
      try {
        this.isLoading = true;
        const response = await axios.get(
          `/billings/${this.$route.params.billId}`
        );
        this.form = {
          ...response.data.data,
          is_revise: !!response.data.data.is_revise,
        };
        if (this.user?.roles[0]?.name !== "Client") {
          this.selectedMonth = this.form.month || this.selectedMonth;
          this.selectedYear = this.form.year || this.selectedYear;
        }
      } catch (error) {
        this.showSnackbar("Failed to load billing data", "error");
        console.error("Error fetching billing:", error);
      } finally {
        this.isLoading = false;
      }
    },
    updateForm() {
      if (this.user?.roles[0]?.name !== "Client") {
        this.form.month = this.selectedMonth;
        this.form.year = this.selectedYear;
      }
    },
    handleFileSelection(file) {
      this.documentFile = file;
      if (file) {
        this.showSnackbar(`Selected file: ${file.name}`, "info");
      }
    },
    async save() {
      if (!this.$refs.form.validate()) {
        this.showSnackbar("Please fill all required fields", "error");
        return;
      }

      try {
        this.isLoading = true;
        this.form.user_id = this.user?.id;
        this.form.site_id = this.site?.id;

        const isEditing = !!this.$route.params.billId;
        const endpoint = isEditing
          ? `/billings/${this.$route.params.billId}`
          : "/billings";
        const method = isEditing ? "patch" : "post";

        // Submit billing data
        const billingResponse = await axios[method](endpoint, this.form);
        const billingId =
          billingResponse.data.data?.id || this.$route.params.billId;

        // Handle file upload (Admin only)
        if (this.documentFile && this.user?.roles[0]?.name !== "Client") {
          await this.handleFileUpload(billingId);
        }

        this.showSnackbar(
          `Billing ${isEditing ? "updated" : "created"} successfully`,
          "success"
        );
        setTimeout(() => this.$router.push("/billings"), 1000);
      } catch (error) {
        this.showSnackbar(
          `Failed to ${
            this.$route.params.billId ? "update" : "create"
          } billing`,
          "error"
        );
        console.error("Error saving billing:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async handleFileUpload(billingId) {
      try {
        const formData = new FormData();
        formData.append("id", billingId);
        formData.append("document_path", this.documentFile);
        formData.append("filename", this.documentFile.name);

        await axios.post("/upload_bill", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.showSnackbar(
          `Document "${this.documentFile.name}" uploaded successfully`,
          "success"
        );
      } catch (error) {
        this.showSnackbar("Failed to upload document", "error");
        console.error("Error uploading document:", error);
        throw error;
      }
    },
    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.footer-text {
  position: fixed;
  bottom: 0;
  width: 100%;
  padding: 8px 0;
}

.text-truncate {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
